import React, { useEffect, useState } from "react";

const initialState = {
  employee_code: "",
  first_name: "",
  last_name: "",
  middle_name: "",
  work_email: "",
  personal_email: "",
  phone: "",
  gender: "",
  date_of_birth: null,
  hire_date: null,
  termination_date: null,
  employment_status: "active",
  employment_type: "full_time",
  department: "",
  job_title: "",
  branch: "",
  cost_center: "",
  pay_frequency: "monthly",
  salary_basis: "monthly",
  base_salary: "",
  hourly_rate: "",
  daily_rate: "",
  currency_code: "NGN",
  tax_id: "",
  pension_pin: "",
  bank_name: "",
  bank_account_name: "",
  bank_account_number: "",
  bank_sort_code: "",
  is_tenant: false,
  annual_rent_paid: "",
  notes: "",
};

function normalizeDateValue(value) {
  return value || "";
}

export default function PayrollEmployeeForm({
  open,
  onClose,
  onSubmit,
  editingEmployee,
}) {
  const [form, setForm] = useState(initialState);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (editingEmployee) {
      setForm({
        ...initialState,
        employee_code: editingEmployee.employee_code || "",
        first_name: editingEmployee.first_name || "",
        last_name: editingEmployee.last_name || "",
        middle_name: editingEmployee.middle_name || "",
        work_email: editingEmployee.work_email || "",
        personal_email: editingEmployee.personal_email || "",
        phone: editingEmployee.phone || "",
        gender: editingEmployee.gender || "",
        date_of_birth: editingEmployee.date_of_birth || null,
        hire_date: editingEmployee.hire_date || null,
        termination_date: editingEmployee.termination_date || null,
        employment_status: editingEmployee.employment_status || "active",
        employment_type: editingEmployee.employment_type || "full_time",
        department: editingEmployee.department || "",
        job_title: editingEmployee.job_title || "",
        branch: editingEmployee.branch || "",
        cost_center: editingEmployee.cost_center || "",
        pay_frequency: editingEmployee.pay_frequency || "monthly",
        salary_basis: editingEmployee.salary_basis || "monthly",
        base_salary: editingEmployee.base_salary ?? "",
        hourly_rate: editingEmployee.hourly_rate ?? "",
        daily_rate: editingEmployee.daily_rate ?? "",
        currency_code: editingEmployee.currency_code || "NGN",
        tax_id: editingEmployee.tax_id || "",
        pension_pin: editingEmployee.pension_pin || "",
        bank_name: editingEmployee.bank_name || "",
        bank_account_name: editingEmployee.bank_account_name || "",
        bank_account_number: editingEmployee.bank_account_number || "",
        bank_sort_code: editingEmployee.bank_sort_code || "",
        is_tenant: !!editingEmployee.is_tenant,
        annual_rent_paid: editingEmployee.annual_rent_paid ?? "",
        notes: editingEmployee.notes || "",
      });
    } else {
      setForm(initialState);
    }
  }, [editingEmployee, open]);

  if (!open) return null;

  function handleChange(e) {
    const { name, value } = e.target;
    setForm((prev) => ({ ...prev, [name]: value }));
  }

  function buildPayload() {
    const salaryBasis = form.salary_basis || "monthly";

    return {
      employee_code: form.employee_code.trim(),
      first_name: form.first_name.trim(),
      last_name: form.last_name.trim(),
      middle_name: form.middle_name.trim() || null,
      work_email: form.work_email.trim() || null,
      personal_email: form.personal_email.trim() || null,
      phone: form.phone.trim() || null,
      gender: form.gender || null,
      date_of_birth: form.date_of_birth || null,
      hire_date: form.hire_date || null,
      termination_date: form.termination_date || null,
      employment_status: form.employment_status || "active",
      employment_type: form.employment_type || "full_time",
      department: form.department.trim() || null,
      job_title: form.job_title.trim() || null,
      branch: form.branch.trim() || null,
      cost_center: form.cost_center.trim() || null,
      pay_frequency: form.pay_frequency || "monthly",
      salary_basis: salaryBasis,
      base_salary:
        salaryBasis === "monthly" ? Number(form.base_salary || 0) : 0,
      hourly_rate: salaryBasis === "hourly" ? Number(form.hourly_rate || 0) : 0,
      daily_rate: salaryBasis === "daily" ? Number(form.daily_rate || 0) : 0,
      currency_code: form.currency_code.trim() || "NGN",
      tax_id: form.tax_id.trim() || null,
      pension_pin: form.pension_pin.trim() || null,
      bank_name: form.bank_name.trim() || null,
      bank_account_name: form.bank_account_name.trim() || null,
      bank_account_number: form.bank_account_number.trim() || null,
      bank_sort_code: form.bank_sort_code.trim() || null,
      is_tenant: !!form.is_tenant,
      annual_rent_paid: form.is_tenant
        ? form.annual_rent_paid === ""
          ? null
          : Number(form.annual_rent_paid)
        : null,
      notes: form.notes.trim() || null,
    };
  }

  async function handleSave(e) {
    e.preventDefault();

    try {
      setSaving(true);
      await onSubmit(buildPayload());
    } finally {
      setSaving(false);
    }
  }

  return (
    <div className="payroll-modal-overlay">
      <div className="payroll-modal">
        <div className="payroll-modal-header">
          <h3>{editingEmployee ? "Edit Employee" : "Add Employee"}</h3>
          <button type="button" className="ghost-btn" onClick={onClose}>
            Close
          </button>
        </div>

        <form onSubmit={handleSave} className="payroll-form">
          <div className="payroll-grid payroll-grid-3">
            <label>
              Employee Code
              <input
                name="employee_code"
                value={form.employee_code}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              First Name
              <input
                name="first_name"
                value={form.first_name}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              Last Name
              <input
                name="last_name"
                value={form.last_name}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              Middle Name
              <input
                name="middle_name"
                value={form.middle_name}
                onChange={handleChange}
              />
            </label>

            <label>
              Work Email
              <input
                name="work_email"
                type="email"
                value={form.work_email}
                onChange={handleChange}
              />
            </label>

            <label>
              Personal Email
              <input
                name="personal_email"
                type="email"
                value={form.personal_email}
                onChange={handleChange}
              />
            </label>

            <label>
              Phone
              <input name="phone" value={form.phone} onChange={handleChange} />
            </label>

            <label>
              Gender
              <input
                name="gender"
                value={form.gender}
                onChange={handleChange}
              />
            </label>

            <label>
              Date of Birth
              <input
                name="date_of_birth"
                type="date"
                value={normalizeDateValue(form.date_of_birth)}
                onChange={handleChange}
              />
            </label>

            <label>
              Hire Date
              <input
                name="hire_date"
                type="date"
                value={normalizeDateValue(form.hire_date)}
                onChange={handleChange}
                required
              />
            </label>

            <label>
              Termination Date
              <input
                name="termination_date"
                type="date"
                value={normalizeDateValue(form.termination_date)}
                onChange={handleChange}
              />
            </label>

            <label>
              Employment Type
              <select
                name="employment_type"
                value={form.employment_type}
                onChange={handleChange}
              >
                <option value="full_time">Full Time</option>
                <option value="part_time">Part Time</option>
                <option value="contract">Contract</option>
                <option value="casual">Casual</option>
                <option value="intern">Intern</option>
              </select>
            </label>

            <label>
              Employment Status
              <select
                name="employment_status"
                value={form.employment_status}
                onChange={handleChange}
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
                <option value="terminated">Terminated</option>
                <option value="on_leave">On Leave</option>
              </select>
            </label>

            <label>
              Department
              <input
                name="department"
                value={form.department}
                onChange={handleChange}
              />
            </label>

            <label>
              Job Title
              <input
                name="job_title"
                value={form.job_title}
                onChange={handleChange}
              />
            </label>

            <label>
              Branch
              <input
                name="branch"
                value={form.branch}
                onChange={handleChange}
              />
            </label>

            <label>
              Cost Center
              <input
                name="cost_center"
                value={form.cost_center}
                onChange={handleChange}
              />
            </label>

            <label>
              Pay Frequency
              <select
                name="pay_frequency"
                value={form.pay_frequency}
                onChange={handleChange}
              >
                <option value="weekly">Weekly</option>
                <option value="biweekly">Biweekly</option>
                <option value="semi_monthly">Semi Monthly</option>
                <option value="monthly">Monthly</option>
              </select>
            </label>

            <label>
              Salary Basis
              <select
                name="salary_basis"
                value={form.salary_basis}
                onChange={handleChange}
              >
                <option value="monthly">Monthly</option>
                <option value="hourly">Hourly</option>
                <option value="daily">Daily</option>
              </select>
            </label>

            <label>
              Base Salary
              <input
                name="base_salary"
                type="number"
                step="0.01"
                value={form.base_salary}
                onChange={handleChange}
              />
            </label>

            <label>
              Hourly Rate
              <input
                name="hourly_rate"
                type="number"
                step="0.01"
                value={form.hourly_rate}
                onChange={handleChange}
              />
            </label>

            <label>
              Daily Rate
              <input
                name="daily_rate"
                type="number"
                step="0.01"
                value={form.daily_rate}
                onChange={handleChange}
              />
            </label>

            <label>
              Currency
              <input
                name="currency_code"
                value={form.currency_code}
                onChange={handleChange}
              />
            </label>

            <label>
              Tax ID
              <input
                name="tax_id"
                value={form.tax_id}
                onChange={handleChange}
              />
            </label>

            <label>
              Pension PIN
              <input
                name="pension_pin"
                value={form.pension_pin}
                onChange={handleChange}
              />
            </label>

            <label>
              Bank Name
              <input
                name="bank_name"
                value={form.bank_name}
                onChange={handleChange}
              />
            </label>

            <label>
              Account Name
              <input
                name="bank_account_name"
                value={form.bank_account_name}
                onChange={handleChange}
              />
            </label>

            <label>
              Account Number
              <input
                name="bank_account_number"
                value={form.bank_account_number}
                onChange={handleChange}
              />
            </label>

            <label>
              Bank Sort Code
              <input
                name="bank_sort_code"
                value={form.bank_sort_code}
                onChange={handleChange}
              />
            </label>

            <label className="check-inline">
              <input
                type="checkbox"
                checked={!!form.is_tenant}
                onChange={(e) =>
                  setForm((p) => ({
                    ...p,
                    is_tenant: e.target.checked,
                    annual_rent_paid: e.target.checked
                      ? p.annual_rent_paid
                      : "",
                  }))
                }
              />
              Employee is a tenant
            </label>

            {form.is_tenant ? (
              <label>
                Annual Rent Paid
                <input
                  type="number"
                  step="0.01"
                  value={form.annual_rent_paid}
                  onChange={(e) =>
                    setForm((p) => ({
                      ...p,
                      annual_rent_paid: e.target.value,
                    }))
                  }
                  placeholder="Enter annual rent paid"
                />
              </label>
            ) : null}
          </div>

          <label>
            Notes
            <textarea
              name="notes"
              rows="4"
              value={form.notes}
              onChange={handleChange}
            />
          </label>

          <div className="payroll-form-actions">
            <button type="button" className="ghost-btn" onClick={onClose}>
              Cancel
            </button>
            <button type="submit" className="primary-btn" disabled={saving}>
              {saving
                ? editingEmployee
                  ? "Updating..."
                  : "Saving..."
                : editingEmployee
                ? "Update Employee"
                : "Save Employee"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}
